import { clsx } from 'clsx'
import type { SquadPreviewPlayer } from '../types'
import styles from './MiniFormationPitch.module.css'

type MiniFormationPitchProps = {
  squadPreview: SquadPreviewPlayer[]
}

const DOT_CLASS: Record<string, string | undefined> = {
  GK:  styles.dotGk,
  DEF: styles.dotDef,
  MID: styles.dotMid,
  FWD: styles.dotFwd,
}

// Default formation shape when squad is empty
const EMPTY_SHAPE = { GK: 1, DEF: 4, MID: 3, FWD: 3 } as const

type PitchSlot = { name: string; position: string; isEmpty: boolean }

function emptyRow(pos: string, count: number): PitchSlot[] {
  return Array.from({ length: count }, () => ({ name: '—', position: pos, isEmpty: true }))
}

function PlayerNode({ slot }: { slot: PitchSlot }) {
  return (
    <div className={styles.playerNode}>
      <div className={clsx(styles.playerDot, slot.isEmpty ? styles.dotEmpty : DOT_CLASS[slot.position])}>
        <span className={styles.dotLabel}>{slot.isEmpty ? '?' : slot.position}</span>
      </div>
      <span className={clsx(styles.playerName, slot.isEmpty && styles.playerNameEmpty)}>
        {slot.name}
      </span>
    </div>
  )
}

export function MiniFormationPitch({ squadPreview }: MiniFormationPitchProps) {
  const starters = squadPreview.filter(p => p.isStarter)
  const bench    = squadPreview.filter(p => !p.isStarter)
  const hasSquad = starters.length > 0

  const fwdRow: PitchSlot[] = hasSquad
    ? starters.filter(p => p.position === 'FWD').map(p => ({ ...p, isEmpty: false }))
    : emptyRow('FWD', EMPTY_SHAPE.FWD)
  const midRow: PitchSlot[] = hasSquad
    ? starters.filter(p => p.position === 'MID').map(p => ({ ...p, isEmpty: false }))
    : emptyRow('MID', EMPTY_SHAPE.MID)
  const defRow: PitchSlot[] = hasSquad
    ? starters.filter(p => p.position === 'DEF').map(p => ({ ...p, isEmpty: false }))
    : emptyRow('DEF', EMPTY_SHAPE.DEF)
  const gkRow: PitchSlot[] = hasSquad
    ? starters.filter(p => p.position === 'GK').map(p => ({ ...p, isEmpty: false }))
    : emptyRow('GK', EMPTY_SHAPE.GK)

  const benchSlots: PitchSlot[] = hasSquad
    ? bench.slice(0, 4).map(p => ({ ...p, isEmpty: false }))
    : Array.from({ length: 4 }, () => ({ name: 'vazio', position: '', isEmpty: true }))

  return (
    <div className={styles.wrapper}>
      <div className={styles.pitch}>
        <div className={styles.pitchGoalTop} />
        <div className={styles.pitchGoalBot} />

        <div className={clsx(styles.playerRow, styles.rowFwd)}>
          {fwdRow.map((slot, i) => <PlayerNode key={i} slot={slot} />)}
        </div>
        <div className={clsx(styles.playerRow, styles.rowMid)}>
          {midRow.map((slot, i) => <PlayerNode key={i} slot={slot} />)}
        </div>
        <div className={clsx(styles.playerRow, styles.rowDef)}>
          {defRow.map((slot, i) => <PlayerNode key={i} slot={slot} />)}
        </div>
        <div className={clsx(styles.playerRow, styles.rowGk)}>
          {gkRow.map((slot, i) => <PlayerNode key={i} slot={slot} />)}
        </div>
      </div>

      <div className={styles.benchStrip}>
        <span className={styles.benchLabel}>Banco</span>
        {benchSlots.map((slot, i) => (
          <div key={i} className={styles.benchPlayer}>
            <div className={clsx(styles.benchDot, slot.isEmpty ? styles.dotEmpty : DOT_CLASS[slot.position])}>
              <span className={styles.benchDotLabel}>{slot.isEmpty ? '—' : slot.position}</span>
            </div>
            <span className={clsx(styles.benchName, slot.isEmpty && styles.benchNameEmpty)}>
              {slot.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
